import { useState } from 'react';
import { ExternalLink, FileText, Pencil, RefreshCw, X } from 'lucide-react';
import type { MeetingNote, MeetingNoteOwner } from '../../lib/meetingNotesApi';
import { useSyncMeetingNoteToKb, useUpdateMeetingNote } from '../../hooks/useMeetingNotes';
import { Markdown } from '../chat/Markdown';
import { ActionItems } from './ActionItems';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</h4>
      {children}
    </div>
  );
}

export function MeetingNoteDetail({
  note,
  owner,
  projectId,
  readOnly = false,
}: {
  note: MeetingNote;
  owner: MeetingNoteOwner;
  projectId?: string;
  /** Hide the edit / KB-sync controls (viewer without edit rights). */
  readOnly?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const syncMutation = useSyncMeetingNoteToKb(owner);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  async function handleSync() {
    setSyncMsg(null);
    try {
      const res = await syncMutation.mutateAsync(note.id);
      setSyncMsg(`Sincronizat în ${res.kbPath}`);
    } catch (err) {
      setSyncMsg(err instanceof Error ? err.message : 'Sincronizarea a eșuat.');
    }
  }

  if (editing) {
    return <EditForm note={note} owner={owner} onDone={() => setEditing(false)} />;
  }

  return (
    <div className="border-t border-gray-100 px-4 py-3">
      <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
        {!readOnly && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
          >
            <Pencil size={12} /> Editează
          </button>
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={handleSync}
            disabled={syncMutation.isPending}
            className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={12} className={syncMutation.isPending ? 'animate-spin' : undefined} />
            Sincronizează în KB
          </button>
        )}
        {note.s3Url && (
          <a
            href={note.s3Url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-brand-700 hover:bg-gray-50"
          >
            <FileText size={12} /> PDF sursă <ExternalLink size={11} />
          </a>
        )}
      </div>

      {syncMsg && <p className="mb-3 text-xs text-gray-500">{syncMsg}</p>}

      {note.ingestStatus === 'FAILED' && note.ingestLog && (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <span className="font-semibold">Procesarea a eșuat.</span> {note.ingestLog}
        </div>
      )}

      {note.summary && (
        <Section title="Rezumat">
          <p className="text-sm text-gray-700">{note.summary}</p>
        </Section>
      )}
      {note.decisions && (
        <Section title="Decizii">
          <Markdown content={note.decisions} />
        </Section>
      )}
      {note.actionItems && (
        <Section title="Acțiuni de urmat">
          <ActionItems content={note.actionItems} projectId={projectId} />
        </Section>
      )}
      {note.keyFacts && (
        <Section title="Fapte cheie">
          <Markdown content={note.keyFacts} />
        </Section>
      )}
      {note.openQuestions && (
        <Section title="Întrebări deschise">
          <Markdown content={note.openQuestions} />
        </Section>
      )}
      <Section title="Notițe">
        <Markdown content={note.content} />
      </Section>
    </div>
  );
}

function EditForm({
  note,
  owner,
  onDone,
}: {
  note: MeetingNote;
  owner: MeetingNoteOwner;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(note.title);
  const [attendees, setAttendees] = useState(note.attendees ?? '');
  const [summary, setSummary] = useState(note.summary ?? '');
  const [content, setContent] = useState(note.content);
  const update = useUpdateMeetingNote(owner);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    await update.mutateAsync({
      noteId: note.id,
      input: {
        title: title.trim(),
        attendees: attendees.trim() || undefined,
        summary: summary.trim() || undefined,
        content: content.trim(),
      },
    });
    onDone();
  }

  return (
    <form onSubmit={submit} className="space-y-3 border-t border-gray-100 bg-gray-50 px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase text-gray-400">Editează notița</span>
        <button
          type="button"
          onClick={onDone}
          aria-label="Anulează"
          className="text-gray-400 hover:text-gray-600"
        >
          <X size={16} />
        </button>
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Titlu *"
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
      <input
        value={attendees}
        onChange={(e) => setAttendees(e.target.value)}
        placeholder="Participanți"
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
      <textarea
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        placeholder="Rezumat"
        rows={2}
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Conținut (markdown) *"
        rows={6}
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Anulează
        </button>
        <button
          type="submit"
          disabled={update.isPending}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          Salvează modificările
        </button>
      </div>
    </form>
  );
}
